import { CONSTELLATION_SIZE } from './constants';
import { paintConstellationFrame } from './painter';
import { advanceConstellationSimulation, createConstellationSimulation } from './simulation';
import type { ConstellationSimulation, ConstellationSimulationInput } from './simulation';
import type { ConstellationPaintStyle, ConstellationProps } from './types';

type ConstellationRenderState = Readonly<{
	paintStyle: ConstellationPaintStyle;
	props: ConstellationProps;
}>;

type CanvasSizeObserver = Readonly<{
	disconnect: () => void;
	syncPixelRatio: () => void;
}>;

const getDevicePixelRatio = (): number => Math.max(window.devicePixelRatio || 1, 1);

const getAudioLevelForPose = ({ active, getAudioLevel, pose }: ConstellationProps): number => {
	if (!active || (pose !== 'listening' && pose !== 'speaking')) {
		return 0;
	}

	return Math.min(1, Math.max(0, getAudioLevel()));
};

const getSimulationInput = ({
	paintStyle,
	props,
}: ConstellationRenderState): ConstellationSimulationInput => ({
	active: props.active,
	audioLevel: getAudioLevelForPose(props),
	dotColors: paintStyle.dotColors,
	paused: props.paused,
	pose: props.pose,
});

export function resizeConstellationCanvas(
	canvas: HTMLCanvasElement,
	context: CanvasRenderingContext2D,
): number {
	const cssWidth = canvas.clientWidth || CONSTELLATION_SIZE;
	const cssHeight = canvas.clientHeight || CONSTELLATION_SIZE;
	const pixelRatio = getDevicePixelRatio();
	const backingWidth = Math.round(cssWidth * pixelRatio);
	const backingHeight = Math.round(cssHeight * pixelRatio);

	if (canvas.width !== backingWidth) {
		canvas.width = backingWidth;
	}

	if (canvas.height !== backingHeight) {
		canvas.height = backingHeight;
	}

	context.setTransform(
		backingWidth / CONSTELLATION_SIZE,
		0,
		0,
		backingHeight / CONSTELLATION_SIZE,
		0,
		0,
	);

	return pixelRatio;
}

const observeCanvasSize = (
	canvas: HTMLCanvasElement,
	context: CanvasRenderingContext2D,
): CanvasSizeObserver => {
	let pixelRatio = 1;

	const resize = (): void => {
		pixelRatio = resizeConstellationCanvas(canvas, context);
	};

	const resizeObserver = new ResizeObserver(resize);

	resize();
	resizeObserver.observe(canvas);

	return {
		disconnect: () => resizeObserver.disconnect(),
		syncPixelRatio: () => {
			if (getDevicePixelRatio() !== pixelRatio) {
				resize();
			}
		},
	};
};

const startAnimationLoop = (
	context: CanvasRenderingContext2D,
	simulation: ConstellationSimulation,
	getRenderState: () => ConstellationRenderState,
	syncPixelRatio: () => void,
): (() => void) => {
	let animationFrameId = 0;

	const drawFrame = (timestampMs: number): void => {
		syncPixelRatio();
		const renderState = getRenderState();
		const frame = advanceConstellationSimulation(
			simulation,
			getSimulationInput(renderState),
			timestampMs,
		);
		paintConstellationFrame(context, frame, {
			glowOpacity: renderState.paintStyle.glowOpacity,
			twg: {
				...renderState.paintStyle.twgGraph,
				opacity: simulation.twgOpacity,
				timeSeconds: simulation.elapsedSeconds,
			},
		});
		animationFrameId = requestAnimationFrame(drawFrame);
	};

	animationFrameId = requestAnimationFrame(drawFrame);

	return () => cancelAnimationFrame(animationFrameId);
};

export const startConstellationCanvas = (
	canvas: HTMLCanvasElement,
	getRenderState: () => ConstellationRenderState,
): (() => void) | undefined => {
	const context = canvas.getContext('2d');
	if (!context) {
		return;
	}

	const simulation = createConstellationSimulation(getSimulationInput(getRenderState()), 0);
	const sizeObserver = observeCanvasSize(canvas, context);
	const stopAnimationLoop = startAnimationLoop(
		context,
		simulation,
		getRenderState,
		sizeObserver.syncPixelRatio,
	);

	return () => {
		stopAnimationLoop();
		sizeObserver.disconnect();
	};
};
